import { IAction, IOperation } from 'types';
import { call, select } from 'typed-redux-saga';
import {
    getOperationData,
    getOmjsContainer,
    getCachedFullContainers,
    getOmjsViewInstance,
    getOperation,
    getOmjsProjectInstance, getAuthToken
} from 'selectors';
import {
    Service,
    bindActionsTyped,
    every, leading, debounce
} from '../service-utils';

import { operation, OperationsService } from '../operations/operations-service';
import { OmjsService } from '../omjs/omjs-service';
import { MapService } from '../map/map-service';
import { FavoritesService } from '../favorites/favorites-service';
import {
    textFilter,
    listTextFilter,
    listObjectsFilter,
    rangeFilter,
    powersFilter, waterFilter
} from './filter-functions';
import { IState } from '../root-reducer';


export interface IContainersOperationData {

}

interface IBaseFilter {
    id: number;
    name: string;
    settingsFilter: {[key: string]: string | boolean;};
    settingsObjects: {[key: string]: string;};
    value: any;
    type: 'text' | 'list_text' | 'range' | 'list_folder' | 'powers' | 'gas' | 'water_supply' | 'sewerage';
    is_custom?: boolean;
}

export interface IFilterListText extends IBaseFilter {
    type: 'list_text';
    values: any[];
    value: any[];
}

export interface IFilterRange extends IBaseFilter {
    type: 'range';
    values: [number, number];
    value: [number, number]
}

export type IFilter = IBaseFilter | IFilterRange | IFilterListText;

export interface IFiltersOperationData {
    [key: string]: IFilter[]
}

const defaultFilterValues = {
    'text': '',
    'list_text': null,
    'range': null,
    'list_folder': null,
    'gas': [ 0, 10, false ],
    'powers': [ 1, 6, false ],
    'water_supply': false,
    'sewerage': false
};

export class ContainersService extends Service {
    static serviceName = 'ContainersService';
    static generatedStopActionTypes: string[] = [];
    static generatedWatchers: Function[] = [];

    @operation('CONTAINERS')
    static* init(instances) {
        let containers: omjs.IContainer[] = [];
        const fullContainers = yield* select(getCachedFullContainers);
        for (let item of instances) {
            let objectsCount = null;
            if (item.get('type') !== 'folder') {
                let omjsLayer = yield* select(getOmjsContainer, item.get('code'), true);
                if (omjsLayer.get('render_type') !== 0) {
                    const layer = fullContainers.find(c => c.get('code') === item.get('code'));
                    objectsCount = layer.vectorLayer?.filteredSource
                        ? layer.vectorLayer.filteredSource?.getFeatures?.().length
                        : omjsLayer.objects.getLength();
                }
            }
            containers.push({ ...item._attributes, objectsCount });
        }

        yield* call(OperationsService.updateData, 'CONTAINERS', containers);
        yield call(ContainersService.updateParentSelected);
    }

    @every
    static* setContainerSelected(action: IAction<{ id_or_code: number | string, selected: boolean }>) {
        let { id_or_code, selected } = action.payload;
        let containers = yield* select(getOperationData, 'CONTAINERS');
        const id = typeof id_or_code === 'string'
            ? containers.find(cont => cont.code === id_or_code)?.id
            : id_or_code;

        const ids = yield* call(OmjsService.switchContainer, id, selected);

        containers = yield* select(getOperationData, 'CONTAINERS');

        yield* call(OperationsService.updateData, 'CONTAINERS', containers.map((cont) => {
            if (ids.indexOf(cont.id) !== -1) {
                cont.selected = selected;
            }
            return { ...cont };
        }));

        yield call(ContainersService.updateParentSelected);
        if (ids.includes(1389) || ids.includes(1924) || ids.includes(2033) || ids.includes(2034)) {
            const connectionsEnabled = {};
            if (ids.includes(1389)) {
                connectionsEnabled['powers'] = selected;
            }
            if (ids.includes(1924)) {
                connectionsEnabled['gas'] = selected;
            }
            if (ids.includes(2033)) {
                connectionsEnabled['waterSupply'] = selected;
            }
            if (ids.includes(2034)) {
                connectionsEnabled['sewerage'] = selected;
            }
            yield call(MapService.updatePowersEnabled, connectionsEnabled);
        }
    }

    @every
    static* updateObjectsCount(action: IAction<string[] | null>) {
        const _codes = action.payload !== null ? action.payload : [ '*' ];
        const containers = yield* select(getOperationData, 'CONTAINERS');
        const fullContainers = yield select(getCachedFullContainers);

        yield call(OperationsService.setData, 'CONTAINERS', containers.map((cont) => {
            if (cont.objectsCount !== null && (_codes.indexOf(cont.code) !== -1 || _codes[0] === '*')) {
                const layer = fullContainers.find(c => c.get('code') === cont.code);
                if (layer) {
                    cont.objectsCount = layer.vectorLayer !== undefined
                        ? layer.vectorLayer.filteredSource.getFeatures().length
                        : layer.objects.getLength();
                }
            }
            return { ...cont };
        }));
    }

    static* setFiltersConfig(filtersConfig) {
        yield call(OperationsService.setData, 'FILTERS', filtersConfig);
    }

    @leading
    @operation('FILTERS')
    static* initFilters() {
        const containers = yield* select(getCachedFullContainers);
        const filtersConfig = yield* select(getOperationData, 'FILTERS');

        let codes = [];

        for (let groupKey of Object.keys(filtersConfig)) {
            filtersConfig[groupKey].forEach((filter) => {
                if (filter.type in defaultFilterValues) {
                    let defaultValue = defaultFilterValues[filter.type];
                    if (Array.isArray(defaultValue)) defaultValue = [ ...defaultValue ];
                    filter.value = defaultValue;
                } else {
                    console.log(groupKey, filter);
                }

                const contCodes = Object.keys(filter.settingsObjects);
                codes = [ ...codes, ...contCodes ];
                const filterContainers = containers.filter(cont => contCodes.includes(cont.get('code')));

                if (filter.type === 'powers') {
                    const contEl = containers.find((c) => c.get('code') === 'ia34');
                    const valueSetEl = new Set<number>();
                    contEl.objects.getArray().forEach((o) => {
                        let v = Number(o.get('power'));
                        if (!Number.isNaN(v)) valueSetEl.add(v);
                    });
                    const maxEl = Math.max(...Array.from(valueSetEl));
                    filter.settingsFilter = { ...filter.settingsFilter, maxPower: maxEl };
                }
                if (filter.type === 'gas') {
                    const contGas = containers.find((c) => c.get('code') === 'ii_obj_gazosnabzh_new');
                    const valueSetGas = new Set<number>();
                    contGas.objects.getArray().forEach((o) => {
                        let v = Number(o.get('throughput_gas'));
                        if (!Number.isNaN(v)) valueSetGas.add(v);
                    });
                    const maxGas = Math.max(...Array.from(valueSetGas));
                    filter.settingsFilter = { ...filter.settingsFilter, maxPower: maxGas };
                }

                if (filter.type === 'list_text') {
                    let uniqueValues = new Set();
                    filterContainers.forEach((cont) => {
                        let field = filter.settingsObjects[cont.get('code')];
                        cont.objects.each((obj) => {
                            let val = typeof obj.get(field) === 'string' ? obj.get(field).trim() : obj.get(field);
                            if (val) {
                                if (filter.settingsFilter && filter.settingsFilter.complex) {
                                    uniqueValues = new Set([ ...uniqueValues, ...val.split(/\s+\|\s+/) ]);
                                } else {
                                    uniqueValues.add(val);
                                }
                            }
                        });
                    });
                    filter.values = [ ...uniqueValues ];
                }

                if (filter.type === 'range') {
                    let values = [];
                    filterContainers.forEach((cont) => {
                        let field = filter.settingsObjects[cont.get('code')];
                        cont.objects.each((obj) => {
                            let val = obj.get(field);
                            if (val) {
                                if (values.length === 0) {
                                    values = [ Math.floor(val), Math.ceil(val) ];
                                } else {
                                    values = [
                                        val < values[0] ? Math.floor(val) : values[0],
                                        val > values[1] ? Math.ceil(val) : values[1]
                                    ];
                                }
                            }
                        });
                    });
                    filter.values = values;
                }
            });
        }
        yield call(OperationsService.setData, 'FILTERS', filtersConfig);
        yield call(ContainersService.makeFilterable, codes);
        yield call(ContainersService.updateObjectsCount, { type: 'updateCount', payload: null });
        yield* call(FavoritesService.validateFavorites);
        yield call(OperationsService.success, 'DATA_LOAD');
        yield call(ContainersService.setUserValues);
    }

    static* setUserValues() {
        const userInfo = yield* select((state: IState) => state.app.user);
        const filtersConfig = yield* select(getOperationData, 'FILTERS');
        const userValues = userInfo?.map_json?.filters_presets || {};
        if (!userValues) return;

        for (let groupKey of Object.keys(filtersConfig)) {
            for (let filter of filtersConfig[groupKey]) {
                if (!(groupKey in userValues && Array.isArray(userValues[groupKey]))) continue;

                const foundPreset = userValues[groupKey].find(uv => uv.name === filter.name);
                if (!foundPreset) continue;

                yield ContainersService.setFilter({
                    type: '_',
                    payload: {
                        code: groupKey,
                        id: filter.id,
                        value: foundPreset.value
                    }
                });
            }
        }
    }

    static* makeFilterable(codes) {
        const view = yield select(getOmjsViewInstance);
        view.getLayers().getArray().forEach((layer) => {
            if (typeof layer.getProjectLayers === 'function' && layer.getProjectLayers()) {
                const container = layer.getProjectLayers().at(0);
                if (codes.includes(container.get('code'))) {
                    let fullSource = layer.getSource() instanceof omjs.ol.source.Cluster
                        ? layer.getSource().getSource()
                        : layer.getSource();

                    layer.fullSource = new omjs.ol.source.Vector({ features: fullSource.getFeatures() });
                    layer.filteredSource = fullSource;
                    container.vectorLayer = layer;
                }
            }
        });
    }

    @every
    static* setFilter(action: IAction<{ code: string, id: number, value: any }>) {
        const { code, id, value } = action.payload;

        const filters = yield* select(getOperationData, 'FILTERS');
        let contCodes = [];
        filters[code] = filters[code].map((filter) => {
            if (filter.id === id) {
                filter.value = value;
                contCodes = [ ...contCodes, ...Object.keys(filter.settingsObjects) ];
            }
            return { ...filter };
        });
        yield call(OperationsService.setData, 'FILTERS', { ...filters });
        if (contCodes.length > 0) {
            yield call(ContainersService.applyFilters, contCodes);
            yield call(ContainersService.updateObjectsCount, { type: 'updateCount', payload: contCodes });
        }
    }

    @debounce(300)
    static* setFilterDebounced(action: IAction<{ code: string, id: number, value: any }>) {
        yield call(ContainersService.setFilter, action);
    }

    static* applyFilters(codes: string[]) {
        const containers = yield* select(getCachedFullContainers);
        const filterContainers = containers.filter(cont => codes.includes(cont.get('code')));
        const filteredIds = {};
        for (let container of filterContainers) {
            const filters = yield call(ContainersService.getContainerFilters, container.get('code'));
            const layer = container['vectorLayer'];
            let features = layer.fullSource.getFeatures();
            for (let filter of filters) {
                if (filter.value !== null && filter.value !== '') {
                    const field = filter.settingsObjects[container.get('code')];
                    switch (filter.type) {
                        case 'text':
                            features = textFilter(features, field, filter.value);
                            break;
                        case 'range':
                            features = rangeFilter(features, field, filter.value);
                            break;
                        case 'list_objects':
                            features = listObjectsFilter(features, field, filter.value, filter.settingsFilter);
                            break;
                        case 'list_text':
                            features = listTextFilter(features, field, filter.value, filter.settingsFilter);
                            break;
                        case 'gas':
                        case 'powers':
                            if (filter.value[2]) {
                                const code = filter.type === 'powers' ? 'ia34' : 'ii_obj_gazosnabzh_new';
                                yield call(ContainersService.setContainerSelected, {
                                    type: 'setContainerSelected',
                                    payload: {
                                        id_or_code: code,
                                        selected: true
                                    }
                                });
                            }
                            features = powersFilter(features, field, filter.value);
                            break;
                        case 'water_supply':
                        case 'sewerage':
                            if (filter.value) {
                                const code = filter.type === 'water_supply' ? 'water_supply' : 'sewerage';
                                yield call(ContainersService.setContainerSelected, {
                                    type: 'setContainerSelected',
                                    payload: {
                                        id_or_code: code,
                                        selected: true
                                    }
                                });
                            }
                            features = waterFilter(features, field, filter.value);
                            break;
                        default: break;
                    }
                }
            }
            filteredIds[container.get('code')] = features.map(f => f.get('obj').getId());
            layer.filteredSource.clear();
            layer.filteredSource.addFeatures(features);
        }
        yield call(OperationsService.updateData, 'FILTERED_IDS', filteredIds);
    }

    static* getContainerFilters(contCode: string) {
        const filters = yield* select(getOperationData, 'FILTERS');
        const contFilters = [];
        for (let key of Object.keys(filters)) {
            for (let filter of filters[key]) {
                if (filter.settingsObjects[contCode] !== undefined) {
                    contFilters.push(filter);
                }
            }
        }
        return contFilters;
    }

    @leading
    static* clearFilters() {
        const filtersOperation: IOperation<IFiltersOperationData> = yield* select(getOperation, 'FILTERS');

        let new_filters = { ...filtersOperation.data };

        for (let [ layer_code, filters ] of Object.entries(new_filters)) {
            for (let f of filters) {
                if (f.value !== null && f.value !== '') {
                    yield ContainersService.setFilter({
                        type: '_',
                        payload: {
                            code: layer_code,
                            id: f.id,
                            value: defaultFilterValues[f.type] || null
                        }
                    });
                }
            }
        }
    }

    @leading
    static* clearFiltersByLayerCode(action: IAction<string>) {
        const clearLayerCode = action.payload;

        const filtersOperation: IOperation<IFiltersOperationData> = yield* select(getOperation, 'FILTERS');

        let new_filters = { ...filtersOperation.data };

        if (Object.prototype.hasOwnProperty.call(new_filters, clearLayerCode)) {
            let filters = new_filters[clearLayerCode];

            for (let f of filters) {
                if (f.value !== null && f.value !== '') {
                    let defaultValue = defaultFilterValues[f.type];
                    if (Array.isArray(defaultValue)) defaultValue = [ ...defaultValue ];
                    yield ContainersService.setFilter({
                        type: '_',
                        payload: {
                            code: clearLayerCode,
                            id: f.id,
                            value: defaultValue || null
                        }
                    });
                }
            }
        } else {
            console.warn(`Не очистили фильтра для слоя ${clearLayerCode} т.к. не смогли его найти`);
        }
    }

    @leading
    static* exportObjects(action: IAction<{ container_code: string; object_ids: number[]; lang?: string; }>) {
        let {
            container_code,
            object_ids,
            lang = 'ru'
        } = action.payload;
        let authToken = yield* select(getAuthToken);
        let project = yield* select(getOmjsProjectInstance);
        let po = project.options;
        let path = `${po.apiUrl + po.mapCode}/layers/${container_code}/export/`;
        let query = `format=xls&custom=1&lng=${lang}&feature_ids=${object_ids.join(',')}`;
        if (authToken) query += `&token=${authToken}`;
        window.open(`${path}?${query}`);
    }

    // @every
    static* updateParentSelected() {
        const containers = yield* select(getOperationData, 'CONTAINERS');
        const parentIndexes = containers.reduce((acc, cont, index) => {
            if ([ 'folder', 'layer', 'virtual' ].includes(cont.type)) {
                acc[cont.id] = index;
                if (cont.type === 'folder') {
                    cont.selected = false;
                }
            }
            return acc;
        }, {});
        const layers = containers.filter(cont => cont.type !== 'folder');
        for (let layer of layers) {
            if (layer.selected) {
                let parentId = layer.parent_id;
                while (parentIndexes[parentId]) {
                    containers[parentIndexes[parentId]].selected = true;
                    parentId = containers[parentIndexes[parentId]].parent_id;
                }
            }
        }

        yield* call(OperationsService.updateData, 'CONTAINERS', [ ...containers ]);
    }

    // @every
    static* _updateParentSelected(code_or_id: string) {
        const containers = yield* select(getOperationData, 'CONTAINERS');


        yield* call(OperationsService.updateData, 'CONTAINERS', [ ...containers ]);
    }
}


export const containersService: ContainersService = new ContainersService();
export const actions = bindActionsTyped(ContainersService, containersService);
